
<template>
<markdown-render v-if="guide" :source="guide"></markdown-render>
</template>

<script>
import { mapState } from 'vuex'
import MarkdownRender from '../../MarkdownRender'

export default {
  components:{
    "markdown-render": MarkdownRender
  },

  computed: {
    ...mapState(
      {
          http: state => state.metadata.backendApi.http[state.metadata.env]
      }
    )
  },
  data(){
    return {
      guide: null
    }
  },

  mounted(){
    this.guide =  "                                                                                       \n\
# Getting started for FP-ATR-ASTRA1 and STEVAL-ASTRA1B                                                \n\
Please follow instructions from [here](https://www.st.com/en/embedded-software/fp-atr-astra1.html) in order to configure properly your device \n\
## TTN account \n\
*Note*: first two steps are optional if default ST device profile is used (*Setup TTN account* and *Setup asset-tracking dashboard*)     \n\
### Setup TTN account \n\
1. Create and login user TTN account from [here](https://eu1.cloud.thethings.network/oauth/login)   \n\
2. Create a new api-key from [here](https://eu1.cloud.thethings.network/console/user/api-keys) and take note of the secret value  \n\
### Setup asset-tracking dashboard \n\
1. Create a new api-key from **settings** view                                                     \n\
2. To simplify the device provisioning, create a LoRa (TTN) device profile from **settings** view        \n\
### Configure device \n\
1. Connect device to local PC via USB and open serial terminal (e.g. TeraTerm)  \n\
2. Take note of device EUI by running `?devicejoinparam` command                                    \n\
3. Configure device with following commands by using the device profile parameters                  \n\
```json\n\
\n\
!joineui-xxxxxxxxxxxxxxx                    // replace x characthers with join eui value\n\
!ntwkkey-xxxxxxxxxxxxxxxxxxxxxxxxxxxxxx     // replace x characthers with network key value\n\
!appkey-xxxxxxxxxxxxxxxxxxxxxxxxxxxxxx      // replace x characthers with application key valu\n\
!sysreset\n\
```\n\
*Note*: network key and application key must be equal\n\n\
### Provision the device \n\
On asset-tracking dashboard, from **devices** view regiester a new *LoRa tracker* device by providing:    \n\
- Human-friendly label                                                                                    \n\
- Device ID (got previously)                                                                              \n\
- Device EUI (got previously)                                                                             \n\
- Device profile (use the default profile provided or that one previously created)                        \n\
- Optional parameters which depend by your setup                                                          \n\
                                                                                                          \n\
                                                                                                          \n\
In case of usage of default device profile, remember to configure the gateway according to the frequency plan used for the region targeted by the selected device profile: \n\
- `default` (i.e. Europe 1 (EU1) - Dublin, Ireland): `EU_863_870_TTN`.                                      \n\
- `default-nam1` (i.e. North America 1 (NAM1) - California, USA): `US_902_928_FSB_1`.                       \n\
- `default-au1` (i.e.  Australia 1 (AU1) - Sydney, Australia): `AU_915_928_FSB_1`.                          \n\
                                                                                                          \n\
## Loriot account                                                                                         \n\
### Setup asset-tracking dashboard                                                                        \n\
1. Create a new api-key from **settings** view                                                            \n\
2. Create a LoRa (Loriot) device profile from **settings** view                                           \n\
### Setup Loriot account                                                                                  \n\
On local pc:                                                                                              \n\
1. Connect device to local PC via USB and open serial terminal (e.g. TeraTerm)                            \n\
2. Take note of device credentials by running `?devicejoinparam` command                                  \n\
\n\
On Loriot console:                                                                                      \n\
3. Configure gateway as reported [here](https://docs.loriot.io/display/LNS5/Register+a+Gateway)           \n\
4. Register new device with credentials got before  as reported [here](https://docs.loriot.io/display/LNS5/Enroll+a+Device)\n\
5. Enable asset-tracking dashboard and Loriot cloud communication creating a new HTTP PUSH output as [reported here](https://docs.loriot.io/display/LNS5/Application+Output): \n\
  - URL: `https://__host__base__url__/__base_path__/data` \n\
  - Authorization: `{... api key value created before ...}`. Right click [here](/assets/howto/loriot-callback.png) to open image in a new tab \n\
                                                                                                          \n\
## MachineQ account                                                                                         \n\
### Setup asset-tracking dashboard                                                                        \n\
1. Create a new api-key from **settings** view                                                            \n\
2. Create a LoRa (MachineQ) device profile from **settings** view                                           \n\
### Setup MachineQ account                                                                                  \n\
On local pc:                                                                                              \n\
1. Connect device to local PC via USB and open serial terminal (e.g. TeraTerm)                            \n\
2. Take note of device credentials by running `?devicejoinparam` command                                  \n\
\n\
On MachineQ Central console:                                                                                      \n\
3. Configure gateway as reported [here](https://support.machineq.com/s/topic/0TO1N000001DreYWAS/gateways)           \n\
4. Register new device with credentials got before  as reported [here](https://support.machineq.com/s/topic/0TO1N000001DredWAC/devices)\n\
5. Enable asset-tracking dashboard and MachineQ cloud communication creating a new WebHook integration as [reported here](https://support.machineq.com/s/article/The-MQcentral-Integrations-Page): \n\
  - URL: `https://__host__base__url__/__base_path__/data` \n\
  - Authorization: `{... api key value created before ...}` \n\
                                                                                                          \n\
## Actility account                                                                                       \n\
### Setup asset-tracking dashboard                                                                        \n\
1. Create a new api-key from **settings** view                                                            \n\
2. Create a LoRa (Actility) device profile from **settings** view                                           \n\
### Setup Actility account                                                                                  \n\
On local pc:                                                                                              \n\
1. Connect device to local PC via USB and open serial terminal (e.g. TeraTerm)                            \n\
2. Take note of device credentials by running `?devicejoinparam` command                                  \n\
\n\
On Actility console:                                                                                      \n\
3. Configure gateway as reported [here](https://youtu.be/gsUCybxDSBA?t=60) in the video            \n\
4. Enable asset-tracking dashboard and Actility cloud communication creating a new Basic HTTPS Push integration as [reported here](https://youtu.be/gsUCybxDSBA?t=96)  in the video : \n\
  - URL: `https://__host__base__url__/__base_path__/data` \n\
  - Authorization: `{... api key value created before ...}` \n\
5. Register new device with credentials got before  as reported [here](https://www.youtube.com/watch?v=gsUCybxDSBA&t=67s)  in the video\n\
      \n\
### Provision the device                                                                                  \n\
On asset-tracking dashboard, from **devices** view regiester a new *LoRa tracker* device by providing:    \n\
- Human-friendly label                                                                                    \n\
- Device ID (EUI got previously - lowercase and without dashes )                                          \n\
- Device profile created before                                                                           \n\
".replace('__host__base__url__', this.http.host ).replace('__base_path__', this.http.basePath );
  }
  
}
</script>